import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { 
  Shield, 
  Download, 
  Trash2, 
  Eye, 
  AlertTriangle,
  CheckCircle,
  Clock,
  Database,
  UserCheck,
  FileText
} from 'lucide-react';

interface DataRequest {
  id: string;
  user_email: string;
  request_type: 'export' | 'deletion' | 'anonymization' | 'access';
  status: 'pending' | 'processing' | 'completed' | 'failed';
  requested_at: string;
  processed_at?: string;
  data_types: string[];
  reason?: string;
  legal_basis?: string;
}

// Map Supabase row to DataRequest object
const mapSupabaseToDataRequest = (row: any): DataRequest => ({
  id: row.id,
  user_email: row.profiles?.email || '', // fallback if no profile loaded
  request_type: row.request_type,
  status: row.status,
  requested_at: row.created_at,
  processed_at: row.processed_at ?? undefined,
  data_types: row.data_types ?? [],
  reason: row.reason ?? undefined,
  legal_basis: row.legal_basis ?? undefined,
});

interface GDPRCompliance {
  data_retention_days: number;
  auto_anonymize_after_days: number;
  requires_consent_renewal: boolean;
  consent_renewal_days: number;
  data_processing_purposes: string[];
  legal_basis: string[];
}

const EnhancedDataManagementTools: React.FC = () => {
  const [activeTab, setActiveTab] = useState('requests');
  const [requests, setRequests] = useState<DataRequest[]>([]);
  const [newRequest, setNewRequest] = useState({
    user_email: '', // will be user_username now
    request_type: 'export' as const,
    data_types: [] as string[],
    reason: '',
    legal_basis: '',
  });
  const [gdprSettings, setGdprSettings] = useState<GDPRCompliance>({
    data_retention_days: 1095,
    auto_anonymize_after_days: 2555,
    requires_consent_renewal: true,
    consent_renewal_days: 365,
    data_processing_purposes: ['content_delivery', 'analytics', 'user_support'],
    legal_basis: ['consent', 'legitimate_interest', 'contract'],
  });
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  // 1. Load requests from DB instead of using mock data.
  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line
  }, []);

  // Fetches the data_export_requests and basic profile info for user_email
  const fetchRequests = async () => {
    setLoading(true);
    try {
      // Join with profiles table to get user email if available
      let { data, error } = await supabase
        .from('data_export_requests')
        .select(
          `
            *,
            profiles:user_id (email)
          `
        )
        .order('created_at', { ascending: false });

      if (error) throw error;

      // NOTE: If your project doesn't store emails in profiles,
      // fallback to a placeholder.
      // If profile column 'email' does not exist, display user_id instead
      const mapped: DataRequest[] = (data || []).map((row: any) => ({
        id: row.id,
        user_email: row.profiles?.email || row.user_id,
        request_type: (row.request_type === "export" ||
                      row.request_type === "deletion" ||
                      row.request_type === "anonymization" ||
                      row.request_type === "access"
           ? row.request_type : 'export') as DataRequest['request_type'],
        status: (row.status === 'pending' ||
                 row.status === 'processing' ||
                 row.status === 'completed' ||
                 row.status === 'failed'
          ? row.status : 'pending') as DataRequest['status'],
        requested_at: row.created_at,
        processed_at: row.processed_at ?? undefined,
        data_types: row.data_types ?? [],
        reason: row.reason ?? undefined,
        legal_basis: row.legal_basis ?? undefined,
      }));
      setRequests(mapped);
    } catch (e: any) {
      toast({
        title: "Error",
        description: "Failed to load requests: " + e.message,
        variant: "destructive",
      });
    }
    setLoading(false);
  };

  const createRequest = async () => {
    if (!newRequest.user_email || newRequest.data_types.length === 0) {
      toast({
        title: "Incomplete Request",
        description: "Please provide username and select data types",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // Lookup user by username
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, username')
        .ilike('username', newRequest.user_email)
        .maybeSingle();

      // Only user_id is required by supabase schema for data_export_requests
      const user_id = profiles?.id ?? null;
      if (!user_id) {
        toast({
          title: "Error",
          description: "No user found with that username",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }
      const { error } = await supabase
        .from('data_export_requests')
        .insert({
          user_id: user_id,
          request_type: newRequest.request_type,
          status: 'pending',
          data_types: newRequest.data_types,
          reason: newRequest.reason ?? null,
          legal_basis: newRequest.legal_basis ?? null,
        });

      if (error) throw error;

      toast({
        title: "Request Created",
        description: "Data management request created successfully",
      });
      setNewRequest({
        user_email: '',
        request_type: 'export',
        data_types: [],
        reason: '',
        legal_basis: ''
      });
      fetchRequests();
    } catch (e: any) {
      toast({
        title: "Error",
        description: "Failed to create data request: " + e.message,
        variant: "destructive",
      });
    }
    setLoading(false);
  };

  // 3. Update the request status in DB
  const processRequest = async (requestId: string) => {
    setLoading(true);
    try {
      const processed_at = new Date().toISOString();
      const { error } = await supabase
        .from('data_export_requests')
        .update({ status: 'completed', processed_at })
        .eq('id', requestId);

      if (error) throw error;

      toast({
        title: "Request Processed",
        description: "Data management request completed successfully",
      });
      fetchRequests();
    } catch (e: any) {
      toast({
        title: "Processing Failed",
        description: "Failed to process data request: " + e.message,
        variant: "destructive",
      });
    }
    setLoading(false);
  };

  const updateGDPRSettings = async () => {
    try {
      // No-op: you'd save these settings in a settings table
      toast({
        title: "Settings Updated",
        description: "GDPR compliance settings updated successfully",
      });
    } catch (error) {
      toast({
        title: "Update Failed",
        description: "Failed to update GDPR settings",
        variant: "destructive",
      });
    }
  };

  // Update: Run real compliance automation on "Run Full Compliance Check"
  const runAutomatedCompliance = async () => {
    setLoading(true);
    try {
      // 1. Find pending requests (simulate more complex logic as needed)
      const { data: pending, error: fetchError } = await supabase
        .from('data_export_requests')
        .select('id, status')
        .eq('status', 'pending');

      if (fetchError) throw fetchError;

      const pendingIds = (pending || []).map((r: any) => r.id);
      let description = '';

      if (pendingIds.length > 0) {
        // 2. Mark all pending as 'processing' for demo/automation purposes
        const { error: updateError } = await supabase
          .from('data_export_requests')
          .update({ status: 'processing', processed_at: new Date().toISOString() })
          .in('id', pendingIds);

        if (updateError) throw updateError;
        description = `Processed ${pendingIds.length} pending request(s).`;
      } else {
        description = 'No pending requests found. Compliance up-to-date!';
      }

      toast({
        title: "Compliance Check Complete",
        description,
      });

      // Refresh request list
      fetchRequests();
    } catch (error: any) {
      toast({
        title: "Compliance Check Failed",
        description: "Failed to run compliance check: " + error.message,
        variant: "destructive",
      });
    }
    setLoading(false);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'processing': return <Clock className="h-4 w-4 text-black" />;
      case 'failed': return <AlertTriangle className="h-4 w-4 text-red-600" />;
      default: return <Clock className="h-4 w-4 text-black" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-white text-green-800';
      case 'processing': return 'bg-white text-black';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-white text-black';
    }
  };

  const getRequestTypeIcon = (type: string) => {
    switch (type) {
      case 'export': return <Download className="h-4 w-4" />;
      case 'deletion': return <Trash2 className="h-4 w-4" />;
      case 'anonymization': return <UserCheck className="h-4 w-4" />;
      case 'access': return <Eye className="h-4 w-4" />;
      default: return <Database className="h-4 w-4" />;
    }
  };

  const dataTypeOptions = [
    'profile',
    'content',
    'comments',
    'ratings',
    'activity',
    'analytics',
    'all'
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Enhanced Data Management & GDPR Compliance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="requests">Data Requests</TabsTrigger>
              <TabsTrigger value="create">Create Request</TabsTrigger>
              <TabsTrigger value="settings">GDPR Settings</TabsTrigger>
              <TabsTrigger value="automation">Automation</TabsTrigger>
            </TabsList>

            <TabsContent value="requests" className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">Data Management Requests</h3>
                <div className="flex gap-2">
                  <Badge variant="outline">
                    {requests.filter(r => r.status === 'pending').length} Pending
                  </Badge>
                  <Badge variant="outline">
                    {requests.filter(r => r.status === 'processing').length} Processing
                  </Badge>
                </div>
              </div>

              <div className="space-y-3">
                {loading && <div className="text-sm">Loading...</div>}
                {requests.map((request) => (
                  <Card key={request.id} className="border-l-4 border-l-blue-500">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {(() => {
                            switch (request.request_type) {
                              case 'export': return <Download className="h-4 w-4" />;
                              case 'deletion': return <Trash2 className="h-4 w-4" />;
                              case 'anonymization': return <UserCheck className="h-4 w-4" />;
                              case 'access': return <Eye className="h-4 w-4" />;
                              default: return <Database className="h-4 w-4" />;
                            }
                          })()}
                          <div>
                            <h4 className="font-medium">{request.user_email}</h4>
                            <p className="text-sm text-black capitalize">
                              {request.request_type} Request
                            </p>
                          </div>
                        </div>
                        <Badge className={
                          request.status === 'completed'
                            ? 'bg-white text-green-800'
                            : request.status === 'processing'
                            ? 'bg-white text-black'
                            : request.status === 'failed'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-white text-black'
                        }>
                          {(() => {
                            switch (request.status) {
                              case 'completed': return <CheckCircle className="h-4 w-4 text-green-600" />;
                              case 'processing': return <Clock className="h-4 w-4 text-black" />;
                              case 'failed': return <AlertTriangle className="h-4 w-4 text-red-600" />;
                              default: return <Clock className="h-4 w-4 text-black" />;
                            }
                          })()}
                          {request.status}
                        </Badge>
                      </div>

                      <div className="grid gap-2 md:grid-cols-2 text-sm mb-3">
                        <div>
                          <span className="font-medium">Data Types:</span> {request.data_types.join(', ')}
                        </div>
                        <div>
                          <span className="font-medium">Legal Basis:</span> {request.legal_basis || 'N/A'}
                        </div>
                        <div>
                          <span className="font-medium">Requested:</span> {new Date(request.requested_at).toLocaleString()}
                        </div>
                        {request.processed_at && (
                          <div>
                            <span className="font-medium">Processed:</span> {new Date(request.processed_at).toLocaleString()}
                          </div>
                        )}
                      </div>

                      {request.reason && (
                        <div className="text-sm mb-3">
                          <span className="font-medium">Reason:</span> {request.reason}
                        </div>
                      )}

                      {request.status === 'pending' && (
                        <Button 
                          size="sm" 
                          disabled={loading}
                          onClick={() => processRequest(request.id)}
                        >
                          Process Request
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}

                {!loading && requests.length === 0 && (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 text-black mx-auto mb-4" />
                    <p className="text-black">No data requests found</p>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="create" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Create Data Management Request</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label htmlFor="user-username">User Username</Label>
                      <Input
                        id="user-username"
                        type="text"
                        value={newRequest.user_email}
                        disabled={loading}
                        onChange={(e) => setNewRequest(prev => ({ ...prev, user_email: e.target.value }))}
                        placeholder="username (not email)"
                      />
                    </div>

                    <div>
                      <Label htmlFor="request-type">Request Type</Label>
                      <Select 
                        value={newRequest.request_type} 
                        onValueChange={(value: any) => setNewRequest(prev => ({ ...prev, request_type: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="export">Data Export</SelectItem>
                          <SelectItem value="deletion">Data Deletion</SelectItem>
                          <SelectItem value="anonymization">Data Anonymization</SelectItem>
                          <SelectItem value="access">Data Access</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label>Data Types</Label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {['profile',
                        'content',
                        'comments',
                        'ratings',
                        'activity',
                        'analytics',
                        'all'
                      ].map((type) => (
                        <label key={type} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={newRequest.data_types.includes(type)}
                            disabled={loading}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setNewRequest(prev => ({ 
                                  ...prev, 
                                  data_types: [...prev.data_types, type] 
                                }));
                              } else {
                                setNewRequest(prev => ({ 
                                  ...prev, 
                                  data_types: prev.data_types.filter(t => t !== type) 
                                }));
                              }
                            }}
                            className="rounded"
                          />
                          <span className="text-sm capitalize">{type}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label htmlFor="legal-basis">Legal Basis</Label>
                      <Input
                        id="legal-basis"
                        value={newRequest.legal_basis}
                        disabled={loading}
                        onChange={(e) => setNewRequest(prev => ({ ...prev, legal_basis: e.target.value }))}
                        placeholder="e.g., GDPR Article 6(1)(a)"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="reason">Reason (Optional)</Label>
                    <Textarea
                      id="reason"
                      value={newRequest.reason}
                      disabled={loading}
                      onChange={(e) => setNewRequest(prev => ({ ...prev, reason: e.target.value }))}
                      placeholder="Additional context for this request..."
                      rows={3}
                    />
                  </div>

                  <Button onClick={createRequest} disabled={loading}>
                    {loading ? 'Creating...' : 'Create Request'}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="settings" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>GDPR Compliance Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label htmlFor="retention-days">Data Retention (Days)</Label>
                      <Input
                        id="retention-days"
                        type="number"
                        value={gdprSettings.data_retention_days}
                        onChange={(e) => setGdprSettings(prev => ({ 
                          ...prev, 
                          data_retention_days: parseInt(e.target.value) 
                        }))}
                      />
                    </div>

                    <div>
                      <Label htmlFor="anonymize-days">Auto-Anonymize After (Days)</Label>
                      <Input
                        id="anonymize-days"
                        type="number"
                        value={gdprSettings.auto_anonymize_after_days}
                        onChange={(e) => setGdprSettings(prev => ({ 
                          ...prev, 
                          auto_anonymize_after_days: parseInt(e.target.value) 
                        }))}
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label htmlFor="consent-renewal">Consent Renewal (Days)</Label>
                      <Input
                        id="consent-renewal"
                        type="number"
                        value={gdprSettings.consent_renewal_days}
                        onChange={(e) => setGdprSettings(prev => ({ 
                          ...prev, 
                          consent_renewal_days: parseInt(e.target.value) 
                        }))}
                      />
                    </div>

                    <div className="flex items-center space-x-2 pt-8">
                      <input
                        type="checkbox"
                        id="require-renewal"
                        checked={gdprSettings.requires_consent_renewal}
                        onChange={(e) => setGdprSettings(prev => ({ 
                          ...prev, 
                          requires_consent_renewal: e.target.checked 
                        }))}
                        className="rounded"
                      />
                      <Label htmlFor="require-renewal">Require Consent Renewal</Label>
                    </div>
                  </div>

                  <Button onClick={updateGDPRSettings}>Update Settings</Button>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="automation" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Automated Compliance</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      Automated compliance tools help ensure GDPR requirements are met automatically.
                    </AlertDescription>
                  </Alert>

                  <div className="grid gap-4 md:grid-cols-2">
                    <Card>
                      <CardContent className="p-4">
                        <h4 className="font-medium mb-2">Consent Monitoring</h4>
                        <p className="text-sm text-black mb-3">
                          Automatically track and renew user consents based on your settings.
                        </p>
                        <Button size="sm" variant="outline">Configure</Button>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardContent className="p-4">
                        <h4 className="font-medium mb-2">Data Retention</h4>
                        <p className="text-sm text-black mb-3">
                          Automatically archive or delete data based on retention policies.
                        </p>
                        <Button size="sm" variant="outline">Configure</Button>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardContent className="p-4">
                        <h4 className="font-medium mb-2">Request Processing</h4>
                        <p className="text-sm text-black mb-3">
                          Automatically process simple data export and deletion requests.
                        </p>
                        <Button size="sm" variant="outline">Configure</Button>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardContent className="p-4">
                        <h4 className="font-medium mb-2">Compliance Reporting</h4>
                        <p className="text-sm text-black mb-3">
                          Generate automated compliance reports and audit logs.
                        </p>
                        <Button size="sm" variant="outline">Configure</Button>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="pt-4 border-t">
                    <Button onClick={runAutomatedCompliance} className="w-full">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Run Full Compliance Check
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default EnhancedDataManagementTools;
